import * as React from "react";
import { cn } from "@/lib/utils";

export interface BlurImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  /** Tailwind classes for the wrapper (controls aspect-ratio, rounding, etc.) */
  wrapperClassName?: string;
  /** Background colour shown while the image is loading. Use a soft tone that
   *  matches the dominant hue of the asset for a smooth blur-up effect. */
  placeholderColor?: string;
  /** Responsive sizes hint — passed straight to the underlying <img>. */
  sizes?: string;
  /** Mark above-the-fold images so the browser prioritises them. */
  priority?: boolean;
  /** When true, the wrapper's aspect-ratio is set from the image's natural
   *  dimensions once it loads — keeping the surrounding layout stable while
   *  guaranteeing the full image is visible without cropping. */
  autoAspect?: boolean;
  /** Called once the underlying image has finished decoding. */
  onLoaded?: () => void;
}

/**
 * Lightweight blur-placeholder image. Renders a soft-coloured wrapper that
 * stays visible until the real image finishes decoding, then fades the image
 * in. No build-time pre-processing required — keeps Vite imports unchanged.
 */
const BlurImage = React.forwardRef<HTMLImageElement, BlurImageProps>(
  (
    {
      src,
      alt,
      className,
      wrapperClassName,
      placeholderColor = "hsl(35 35% 88%)",
      sizes,
      priority,
      autoAspect,
      onLoaded,
      ...rest
    },
    ref,
  ) => {
    const [loaded, setLoaded] = React.useState(false);
    const [ratio, setRatio] = React.useState<string | undefined>(undefined);

    return (
      <div
        className={cn("relative overflow-hidden", wrapperClassName)}
        style={{
          backgroundColor: placeholderColor,
          ...(autoAspect && ratio ? { aspectRatio: ratio } : null),
        }}
      >
        <div
          aria-hidden
          className={cn(
            "absolute inset-0 transition-opacity duration-700 ease-out backdrop-blur-md",
            loaded ? "opacity-0" : "opacity-100",
          )}
          style={{
            background: `linear-gradient(135deg, ${placeholderColor} 0%, hsl(35 25% 78%) 100%)`,
          }}
        />
        <img
          ref={ref}
          src={src}
          alt={alt}
          sizes={sizes}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={priority ? "high" : "low"}
          onLoad={(e) => {
            setLoaded(true);
            if (autoAspect) {
              const img = e.currentTarget;
              if (img.naturalWidth && img.naturalHeight) {
                setRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
              }
            }
            onLoaded?.();
          }}
          onError={(e) => {
            const img = e.currentTarget;
            if (img.dataset.fallbackApplied === "true") return;
            // eslint-disable-next-line no-console
            console.error("[BlurImage] Failed to load image", { src, alt });
            img.dataset.fallbackApplied = "true";
            img.src = "/placeholder.svg";
            setLoaded(true);
          }}
          className={cn(
            "relative w-full h-full object-cover transition-opacity duration-700 ease-out",
            loaded ? "opacity-100" : "opacity-0",
            className,
          )}
          {...rest}
        />
      </div>
    );
  },
);
BlurImage.displayName = "BlurImage";

export default BlurImage;
